import { IPoint2d } from '@/editor/math/IPoint2d';
import { Line2d } from '@/editor/math/Line2d';
import { LineSeg2d } from '@/editor/math/LineSeg2d';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { genShapeHash } from '@/editor/utils/hash';
import { intersectArcSegAndLineSeg, intersectCommonShape } from '@/editor/math/shape/intesection';
import { IShape2d, IShapeSeg2d } from '@/editor/types/shape';
import { genPointHash } from '@/editor/utils/hash';
import { thresholdValue, thresholdValueMagnitude } from '@/editor/math/const';
import { RTree } from '@/editor/utils/RTree';
import {
  EAbsorbPointType,
  EAbsorbSource,
  ESublineType,
  IAbsorbPointInfo,
  ISublineInfo,
  IWallAbsorbUniformStore,
  IWallAbsorbUniformStoreOptions
} from '@/editor/types/absorb';
import {
  getDisplayAbsorbPointTypePriority,
  getExtremePointNormalLine,
  getPreferredDisplayAbsorbPointType,
  getChordEndpointNormalLines,
  getTangentFootPoint,
  isFloorPlanSubline
} from './utils';
import { ArcSeg2d } from '@/editor/math/arcSeg2d';
import { getFootRatio, getPointFromRatio, getShapeSeg, getShape, pointOnSeg } from '@/editor/math/shape/other';
import { Circle2d } from '@/editor/math/circle2d';
import { normalizeAngle } from '@/editor/math/utils';
import { ANGLE_THRESHOLD } from '@/editor/constant/math';
import { EEntityType } from '@/editor/types';
import { globalStores } from '@/editor/stores';
import { MAX_SAGITTA, MIN_SAGITTA } from '@/editor/constant/wall';
import { FloatingPoint } from '@/editor/math/number';
import {
  filterVisibleInfos,
  getAbsorbFilterBounds,
  getAbsorbViewportBounds,
  TAbsorbViewportBounds,
  TAbsorbViewportBoundsProvider
} from '../viewportFilter';

interface IAbsorbWallInfo {
  id: string;
  start: IPoint2d;
  end: IPoint2d;
  center?: IPoint2d;
  clockwise?: boolean;
  /** 该线的来源，决定它与真实墙体竞争吸附点时的优先级 */
  source: EAbsorbSource;

  seg: IShapeSeg2d;
  shape: IShape2d;

  startNormalLine: Line2d; // 起始点法线
  endNormalLine: Line2d; // 终点法线
  startChordNormalLine?: Line2d; // 弧形墙弦起点垂线
  endChordNormalLine?: Line2d; // 弧形墙弦终点垂线
}

interface IAbsorbPointRecord extends IAbsorbPointInfo {
  // 真实吸附点选择仍以交点为优先级，显示样式类型单独计算。
  hasCrossPoint: boolean;
}

export interface ICalculateOptions {
  forceOrthogonalAbsorb?: boolean;
  extraWallInfos?: IAbsorbWallInfo[];
}

type TWallType = EEntityType.Wall | EEntityType.SeparateWall;
type TEndpointNormalLineInfo = {
  wallId: string;
  type: ESublineType.StartNormalLine | ESublineType.EndNormalLine;
  line: Line2d;
  origin: IPoint2d;
  source: EAbsorbSource;
};
interface IWallNormalLineOptions {
  includeChordEndpointNormalLines?: boolean;
}

export class WallPointAbsorbCalculator {
  private _type: TWallType;
  private _wallStore: IWallAbsorbUniformStore;
  private _wallInfos: IAbsorbWallInfo[] = [];
  private _extraWallInfos: IAbsorbWallInfo[] = [];

  // 正交线，绘制终点或移动端点时需要添加
  private _orthogonalOriginPoint: IPoint2d | null = null;
  private _horizontalLine: Line2d | null = null;
  private _verticalLine: Line2d | null = null;
  private _forceOrthogonalAbsorb: boolean = false;
  private _currentAbsorbOrthogonal: ESublineType.HorizontalLine | ESublineType.VerticalLine | null = null;

  private _pointRTree: RTree<{ pointHash: number }> = new RTree();

  /** 当前 calculate 的视口过滤矩形，null 表示不过滤。每个 calculate 入口设置一次 */
  private _filterBounds: TAbsorbViewportBounds = null;
  private _getViewportBounds: TAbsorbViewportBoundsProvider;

  constructor(
    wallStore: IWallAbsorbUniformStore,
    type: TWallType = EEntityType.Wall,
    getViewportBounds: TAbsorbViewportBoundsProvider = getAbsorbViewportBounds
  ) {
    this._wallStore = wallStore;
    this._type = type;
    this._getViewportBounds = getViewportBounds;
  }

  get orthogonalOriginPoint() {
    return this._orthogonalOriginPoint;
  }

  private get _totalWallInfos() {
    // 只吸附视口内可见的墙；extraWallInfos 是按光标邻近现取的 DXF 底图线，本就在视口内，不参与过滤
    const visibleWallInfos = filterVisibleInfos(this._wallInfos, this._filterBounds);
    if (!this._extraWallInfos.length) return visibleWallInfos;
    return [...visibleWallInfos, ...this._extraWallInfos];
  }

  /**
   * 参考点会并入过滤矩形，保证「参考点所在处的候选不被误杀」。
   * shift 强制正交时 rawPoint 会被投影到正交线原点所在的行/列上，该原点可能已被平移出屏幕。
   */
  private _updateFilterBounds(referencePoints: IPoint2d[]) {
    this._filterBounds = getAbsorbFilterBounds(this._getViewportBounds(), referencePoints);
  }

  private get _isSeparateWall() {
    return this._type === EEntityType.SeparateWall;
  }

  updateWallData(options?: IWallAbsorbUniformStoreOptions) {
    this._wallInfos = this._wallStore.getUniformShapeWallInfos(options).map((item) => {
      const seg = getShapeSeg(item);
      const [startNormalLine, endNormalLine] = getExtremePointNormalLine(seg);
      const chordEndpointNormalLines = item.center ? getChordEndpointNormalLines(item.start, item.end) : null;

      return {
        id: item.id,
        start: item.start,
        end: item.end,
        center: item.center,
        clockwise: item.clockwise,
        source: EAbsorbSource.Wall,
        seg,
        shape: getShape(item),
        startNormalLine,
        endNormalLine,
        startChordNormalLine: chordEndpointNormalLines?.[0],
        endChordNormalLine: chordEndpointNormalLines?.[1]
      };
    });
  }

  setOrthogonalData(point: IPoint2d) {
    this._orthogonalOriginPoint = point;
    this._horizontalLine = new Line2d({
      parametric: {
        point,
        direction: { x: 1, y: 0 }
      }
    });
    this._verticalLine = new Line2d({
      parametric: {
        point,
        direction: { x: 0, y: 1 }
      }
    });
  }
  clearOrthogonalData() {
    this._orthogonalOriginPoint = null;
    this._horizontalLine = null;
    this._verticalLine = null;
  }

  private _addedLineHash = new Set<string>(); // 记录已经添加的墙线段/墙延长线，避免重复添加
  private _sublineInfos: ISublineInfo[] = [];
  calculate(rawPoint: IPoint2d, options?: ICalculateOptions): IAbsorbPointInfo | null {
    this.clear();

    this._forceOrthogonalAbsorb = options?.forceOrthogonalAbsorb ?? false; // 是否强制吸附正交线
    this._extraWallInfos = options?.extraWallInfos ?? [];
    this._updateFilterBounds([rawPoint]);

    // ================== 获取所有邻近辅助线，并计算对应的垂足/墙端点 ==================
    // 先添加正交线线
    this.prepareOrthogonalSubline(rawPoint);

    // 再添加墙线段/墙延长线
    this.prepareWallSubline(rawPoint);

    // 开始计算交点
    this.sublineIntersect(rawPoint);

    // ================== 对吸附点列表进行排序 ==================
    const sortedAbsorbPointList = this.getSortedAbsorbPointList(rawPoint);

    // 返回第一个吸附点
    return sortedAbsorbPointList.length > 0 ? sortedAbsorbPointList[0] : null;
  }

  calculateArcAbsorb(params: { arcStart: IPoint2d; arcEnd: IPoint2d; rawPoint: IPoint2d }): IAbsorbPointInfo | null {
    this.clear();
    const dynamicAbsorbDistance = globalStores.dynamicAbsorbDistance;

    const { arcStart, arcEnd, rawPoint } = params;
    const arcMidPoint = this.getCurrentArcMidPoint(arcStart, arcEnd, rawPoint);

    // 靠近中点则按中点吸附
    const absorbReferencePoint =
      arcMidPoint && Point2dUtils.distanceTo(arcMidPoint, rawPoint) < dynamicAbsorbDistance ? arcMidPoint : rawPoint;

    // 先检测半圆吸附，有就立即返回
    const halfAbsorbCenter = Point2dUtils.getCenter(arcStart, arcEnd);
    const halfAbsorbRadius = Point2dUtils.distanceTo(halfAbsorbCenter, arcStart);
    const halfAbsorbCircle = new Circle2d({ center: halfAbsorbCenter, radius: halfAbsorbRadius });
    const halfAbsorbDistance = halfAbsorbCircle.pointDistance(absorbReferencePoint);
    if (Math.abs(halfAbsorbDistance) < dynamicAbsorbDistance) {
      const rawPointAngle = halfAbsorbCircle.getPointAngle(absorbReferencePoint);
      const startPointAngle = halfAbsorbCircle.getPointAngle(arcStart);
      const endPointAngle = halfAbsorbCircle.getPointAngle(arcEnd);
      const endPointToRawPointAngle = normalizeAngle(rawPointAngle - endPointAngle);

      let absorbAngle = rawPointAngle;
      if (endPointToRawPointAngle < ANGLE_THRESHOLD) {
        absorbAngle = endPointAngle + ANGLE_THRESHOLD * 2;
      } else if (endPointToRawPointAngle <= Math.PI && Math.PI - endPointToRawPointAngle < ANGLE_THRESHOLD) {
        absorbAngle = endPointAngle + Math.PI - ANGLE_THRESHOLD * 2;
      } else if (endPointToRawPointAngle > Math.PI && endPointToRawPointAngle - Math.PI < ANGLE_THRESHOLD) {
        absorbAngle = endPointAngle + Math.PI + ANGLE_THRESHOLD * 2;
      } else if (Math.PI * 2 - endPointToRawPointAngle < ANGLE_THRESHOLD) {
        absorbAngle = endPointAngle + Math.PI * 2 - ANGLE_THRESHOLD * 2;
      }
      absorbAngle = normalizeAngle(absorbAngle);

      const clockwise = endPointToRawPointAngle >= 0 && endPointToRawPointAngle <= Math.PI;

      const halfAbsorbFoot = halfAbsorbCircle.getPointOnCircle(absorbAngle);
      const halfAbsorbArcSeg = new ArcSeg2d({
        center: halfAbsorbCenter,
        radius: halfAbsorbRadius,
        startAngle: startPointAngle,
        endAngle: endPointAngle,
        clockwise
      });

      return {
        absorbPoint: halfAbsorbFoot,
        absorbPointType: EAbsorbPointType.Foot,
        sublineInfos: new Set([
          {
            wallId: '',
            type: ESublineType.WallSeg,
            absorbPoint: halfAbsorbFoot,
            absorbPointType: EAbsorbPointType.Foot,
            shape: halfAbsorbArcSeg
          }
        ])
      };
    }

    this._updateFilterBounds([arcStart, arcEnd, absorbReferencePoint]);

    // 获取所有能相切的弧形墙
    this.prepareCircleSubline(arcStart, arcEnd, absorbReferencePoint);

    this.prepareCurrentArcMidPointSubline(arcStart, arcEnd, absorbReferencePoint);

    // ================== 获取所有邻近辅助线，并计算对应的垂足/墙端点 ==================
    // 再添加墙线段/墙延长线
    this.prepareWallSubline(absorbReferencePoint, { includeChordEndpointNormalLines: false });

    // 开始计算交点
    this.sublineIntersect(absorbReferencePoint);

    // ================== 对吸附点列表进行排序 ==================
    const sortedAbsorbPointList = this.getSortedAbsorbPointList(absorbReferencePoint);

    // 返回第一个吸附点
    return sortedAbsorbPointList.length > 0 ? sortedAbsorbPointList[0] : null;
  }

  calculateAbsorbByLineSeg(
    startPoint: IPoint2d,
    endPoint: IPoint2d,
    options?: ICalculateOptions
  ): IAbsorbPointInfo | null {
    this.clear();

    this._extraWallInfos = options?.extraWallInfos ?? [];
    this._updateFilterBounds([startPoint, endPoint]);

    this._prepareWallSubLineByLine(startPoint, endPoint);

    this._sublineIntersectWhenCalculateByLineSeg(startPoint, endPoint);

    // 测试
    const neiborAbsorbPointList = Array.from(this.absorbPointMap.values());
    const absorbPointList = neiborAbsorbPointList.map((item) => {
      return {
        ...item,
        weight: this.evaluatePointWeight(Array.from(item.sublineInfos))
      };
    });

    return absorbPointList?.[0] ?? null;
  }

  private prepareWallSubline(rawPoint: IPoint2d, options?: IWallNormalLineOptions) {
    const wallInfos = this._totalWallInfos;
    const wallFootRatio = wallInfos.map((item) => getFootRatio(item.seg, rawPoint));
    let sublineInfos: ISublineInfo[] = [];
    const addedLineHash = this._addedLineHash;
    const dynamicAbsorbDistance = globalStores.dynamicAbsorbDistance;

    // 计算墙线段/墙切线
    wallInfos.forEach((wallInfo, index) => {
      const wallSeg = wallInfo.seg;
      const footRatio = wallFootRatio[index];
      const footPoint = getTangentFootPoint(wallSeg, rawPoint, footRatio);

      this.prepareWallMidPointSubline(rawPoint, wallInfo, sublineInfos);

      if (Point2dUtils.distanceTo(footPoint, rawPoint) > dynamicAbsorbDistance) {
        return;
      }

      const nearStart = Point2dUtils.distanceTo(rawPoint, wallInfo.seg.start) < dynamicAbsorbDistance;

      const nearEnd = Point2dUtils.distanceTo(rawPoint, wallInfo.seg.end) < dynamicAbsorbDistance;

      const lineHash = genShapeHash(wallInfo.shape);
      const isAddedLine = this._addedLineHash.has(lineHash);
      if (nearStart || nearEnd) {
        if (nearStart) {
          // 捕获了墙起点
          sublineInfos.push({
            wallId: wallInfo.id,
            type: ESublineType.WallSeg,
            absorbPoint: wallSeg.start,
            absorbPointType: EAbsorbPointType.Start,
            shape: wallInfo.seg,
            source: wallInfo.source
          });
        }

        if (nearEnd) {
          // 捕获了墙终点
          sublineInfos.push({
            wallId: wallInfo.id,
            type: ESublineType.WallSeg,
            absorbPoint: wallSeg.end,
            absorbPointType: EAbsorbPointType.End,
            shape: wallInfo.seg,
            source: wallInfo.source
          });
        }
      } else {
        if (wallInfo.seg.pointOnSegment(footPoint) && footRatio > 0 && footRatio < 1 && !isAddedLine) {
          // 捕获了墙线段上的垂点
          sublineInfos.push({
            wallId: wallInfo.id,
            type: ESublineType.WallSeg,
            absorbPoint: footPoint,
            absorbPointType: EAbsorbPointType.Foot,
            shape: wallInfo.seg,
            source: wallInfo.source
          });
        } else {
          if (footRatio >= 0.5 && !isAddedLine) {
            // 捕获了墙延长线的垂点
            const rayOrigin = wallInfo.center ? wallInfo.end : wallInfo.start;
            const shape = wallInfo.center ? new Line2d({ points: { p1: rayOrigin, p2: footPoint } }) : wallInfo.shape;
            sublineInfos.push({
              wallId: wallInfo.id,
              type: ESublineType.WallExtensionLine,
              absorbPoint: footPoint,
              origin: rayOrigin,
              absorbPointType: EAbsorbPointType.Foot,
              shape,
              source: wallInfo.source,
              extra: getArcCenterlineExtra(wallInfo.seg)
            });
          } else if (footRatio < 0.5 && !isAddedLine) {
            // 捕获了墙延长线的垂点
            const rayOrigin = wallInfo.center ? wallInfo.start : wallInfo.end;
            const shape = wallInfo.center ? new Line2d({ points: { p1: rayOrigin, p2: footPoint } }) : wallInfo.shape;
            sublineInfos.push({
              wallId: wallInfo.id,
              type: ESublineType.WallExtensionLine,
              origin: rayOrigin,
              absorbPoint: footPoint,
              absorbPointType: EAbsorbPointType.Foot,
              shape,
              source: wallInfo.source,
              extra: getArcCenterlineExtra(wallInfo.seg)
            });
          }
        }
      }

      addedLineHash.add(lineHash);
    });

    // 计算相邻的端点法线，需要排除法线与墙线段/墙延长线重合的情况
    wallInfos.forEach((wallInfo, index) => {
      const wallSeg = wallInfo.seg;
      const footRatio = wallFootRatio[index];
      const footPoint = getPointFromRatio(wallSeg, footRatio);

      // 计算相邻的端点法线，超出墙体捕获距离时，才计算端点法线
      if (!this._isSeparateWall && Point2dUtils.distanceTo(footPoint, rawPoint) < dynamicAbsorbDistance) return;

      const normalLineInfos = this.getWallNormalLineInfos(wallInfo, options).sort(
        (a, b) => Point2dUtils.distanceTo(rawPoint, a.origin) - Point2dUtils.distanceTo(rawPoint, b.origin)
      );
      for (const normalLineInfo of normalLineInfos) {
        const normalLineHash = genShapeHash(normalLineInfo.line);
        if (addedLineHash.has(normalLineHash)) continue;

        addedLineHash.add(normalLineHash);
        const normalLineFootPoint = normalLineInfo.line.pointProjectOnLine(rawPoint);
        if (Point2dUtils.distanceTo(normalLineFootPoint, rawPoint) < dynamicAbsorbDistance) {
          sublineInfos.push({
            wallId: normalLineInfo.wallId,
            type: normalLineInfo.type,
            absorbPoint: normalLineFootPoint,
            origin: normalLineInfo.origin,
            absorbPointType: EAbsorbPointType.Foot,
            shape: normalLineInfo.line,
            source: normalLineInfo.source
          });
        }
      }
    });

    // 先获取端点/中点吸附的点，不参与后续相交计算
    sublineInfos = sublineInfos.filter((sublineInfo) => {
      let preserve = true;
      if (sublineInfo.absorbPointType === EAbsorbPointType.Start) {
        this.addAbsorbPoint(sublineInfo.absorbPoint, sublineInfo);
        preserve = false;
      } else if (sublineInfo.absorbPointType === EAbsorbPointType.End) {
        this.addAbsorbPoint(sublineInfo.absorbPoint, sublineInfo);
        preserve = false;
      } else if (sublineInfo.absorbPointType === EAbsorbPointType.Center) {
        this.addAbsorbPoint(sublineInfo.absorbPoint, sublineInfo);
        preserve = false;
      }
      return preserve;
    });

    this._sublineInfos.push(...sublineInfos);
  }

  private prepareWallMidPointSubline(rawPoint: IPoint2d, wallInfo: IAbsorbWallInfo, sublineInfos: ISublineInfo[]) {
    const midPoint = getPointFromRatio(wallInfo.seg, 0.5);
    if (Point2dUtils.distanceTo(midPoint, rawPoint) > globalStores.dynamicAbsorbDistance) return;

    sublineInfos.push({
      wallId: wallInfo.id,
      type: ESublineType.WallSeg,
      absorbPoint: midPoint,
      absorbPointType: EAbsorbPointType.Center,
      shape: wallInfo.seg,
      source: wallInfo.source
    });
  }

  private getCurrentArcMidPoint(arcStart: IPoint2d, arcEnd: IPoint2d, rawPoint: IPoint2d): IPoint2d | null {
    const sagitta = ArcSeg2d.calculateSagitta(arcStart, arcEnd, rawPoint);

    if (!FloatingPoint.between(Math.abs(sagitta), MIN_SAGITTA, MAX_SAGITTA)) return null;

    return new ArcSeg2d({ start: arcStart, end: arcEnd, sagitta }).getMidPoint();
  }

  private prepareCurrentArcMidPointSubline(arcStart: IPoint2d, arcEnd: IPoint2d, arcMidPoint: IPoint2d) {
    if (Point2dUtils.equals(arcStart, arcEnd)) return;

    const chordMidPoint = Point2dUtils.getCenter(arcStart, arcEnd);
    const chordDirection = Point2dUtils.getUnitDirection(arcStart, arcEnd);
    const midPointDirection = Point2dUtils.rotateDirection(chordDirection, Math.PI / 2);
    const midPointLine = new Line2d({
      parametric: {
        point: chordMidPoint,
        direction: midPointDirection
      }
    });

    this._sublineInfos.push({
      wallId: '',
      type: ESublineType.WallExtensionLine,
      absorbPoint: midPointLine.pointProjectOnLine(arcMidPoint),
      origin: chordMidPoint,
      absorbPointType: EAbsorbPointType.Line,
      shape: midPointLine,
      extra: {
        skipFallbackAbsorb: true
      }
    });
  }

  private prepareOrthogonalSubline(rawPoint: IPoint2d) {
    if (this._horizontalLine && this._verticalLine) {
      const dynamicAbsorbDistance = globalStores.dynamicAbsorbDistance;
      const horizontalLineFootPoint = this._horizontalLine.pointProjectOnLine(rawPoint);
      const verticalLineFootPoint = this._verticalLine.pointProjectOnLine(rawPoint);
      const toHorizontalLineDistance = Point2dUtils.distanceTo(horizontalLineFootPoint, rawPoint);
      const toVerticalLineDistance = Point2dUtils.distanceTo(verticalLineFootPoint, rawPoint);

      const addHorizontalLine = toHorizontalLineDistance < dynamicAbsorbDistance;
      const addVerticalLine = !addHorizontalLine && toVerticalLineDistance < dynamicAbsorbDistance;

      if (addHorizontalLine) {
        this._sublineInfos.push({
          wallId: '',
          type: ESublineType.HorizontalLine,
          absorbPoint: horizontalLineFootPoint,
          absorbPointType: EAbsorbPointType.Foot,
          shape: this._horizontalLine
        });
        this._addedLineHash.add(genShapeHash(this._horizontalLine));
        this._currentAbsorbOrthogonal = ESublineType.HorizontalLine;
      } else if (addVerticalLine) {
        this._sublineInfos.push({
          wallId: '',
          type: ESublineType.VerticalLine,
          absorbPoint: verticalLineFootPoint,
          absorbPointType: EAbsorbPointType.Foot,
          shape: this._verticalLine
        });
        this._addedLineHash.add(genShapeHash(this._verticalLine));
        this._currentAbsorbOrthogonal = ESublineType.VerticalLine;
      } else {
        this._currentAbsorbOrthogonal = null;
      }
    }
  }

  private getWallNormalLineInfos(
    wallInfo: IAbsorbWallInfo,
    options?: IWallNormalLineOptions
  ): TEndpointNormalLineInfo[] {
    const normalLineInfos: TEndpointNormalLineInfo[] = [
      {
        wallId: wallInfo.id,
        type: ESublineType.StartNormalLine,
        line: wallInfo.startNormalLine,
        origin: wallInfo.start,
        source: wallInfo.source
      },
      {
        wallId: wallInfo.id,
        type: ESublineType.EndNormalLine,
        line: wallInfo.endNormalLine,
        origin: wallInfo.end,
        source: wallInfo.source
      }
    ];

    const includeChordEndpointNormalLines = options?.includeChordEndpointNormalLines ?? true;
    if (includeChordEndpointNormalLines && wallInfo.startChordNormalLine && wallInfo.endChordNormalLine) {
      normalLineInfos.push(
        {
          wallId: wallInfo.id,
          type: ESublineType.StartNormalLine,
          line: wallInfo.startChordNormalLine,
          origin: wallInfo.start,
          source: wallInfo.source
        },
        {
          wallId: wallInfo.id,
          type: ESublineType.EndNormalLine,
          line: wallInfo.endChordNormalLine,
          origin: wallInfo.end,
          source: wallInfo.source
        }
      );
    }

    return normalLineInfos;
  }

  // 添加相切圆数据
  private prepareCircleSubline(arcStart: IPoint2d, arcEnd: IPoint2d, rawPoint: IPoint2d) {
    const dynamicAbsorbDistance = globalStores.dynamicAbsorbDistance;
    const chordAngle = Math.atan2(arcEnd.y - arcStart.y, arcEnd.x - arcStart.x);
    const chordLength = Point2dUtils.distanceTo(arcStart, arcEnd);
    this._totalWallInfos.forEach((item) => {
      if (!(item.seg instanceof ArcSeg2d)) return;

      const startOnSeg = pointOnSeg(item.seg, arcStart);
      const endOnSeg = pointOnSeg(item.seg, arcEnd);
      if (!startOnSeg && !endOnSeg) return;

      const chord = new LineSeg2d(arcStart, arcEnd);
      const intersected = intersectArcSegAndLineSeg(item.seg, chord);
      if (intersected?.length !== 1) return;

      // 求吸附圆
      const normalDirection = Point2dUtils.getDirection(item.seg.center, intersected[0]); // 两圆心连线方向
      const normalPointAngle = Point2dUtils.getAngle({ x: 0, y: 0 }, normalDirection);
      const chordAngleDiff = normalizeAngle(normalPointAngle - chordAngle);

      const absorbCircleRadius = chordLength / 2 / Math.cos(chordAngleDiff);
      const absorbCircleRadiusCenter = Point2dUtils.addDistance(
        item.seg.center,
        absorbCircleRadius + item.seg.radius,
        normalDirection
      );
      const absorbCircle = new Circle2d({ center: absorbCircleRadiusCenter, radius: absorbCircleRadius });
      const distanceToCircle = absorbCircle.pointDistance(rawPoint);
      if (Math.abs(distanceToCircle) > dynamicAbsorbDistance) return;

      const footPoint = absorbCircle.getPointOnCircle(absorbCircle.getPointAngle(rawPoint));
      this._sublineInfos.push({
        wallId: item.id,
        type: ESublineType.Circle,
        absorbPoint: footPoint,
        absorbPointType: EAbsorbPointType.Foot,
        shape: absorbCircle,
        source: item.source,
        extra: {
          arcCenterline: item.seg
        }
      });
    });
  }

  private sublineIntersect(rawPoint: IPoint2d) {
    const dynamicAbsorbDistance = globalStores.dynamicAbsorbDistance;

    // 开始计算交点
    const sublineInfos = this._sublineInfos;
    for (let i = 0; i < sublineInfos.length; i++) {
      const sublineInfo = sublineInfos[i];
      let intersected = false;

      for (let j = i + 1; j < sublineInfos.length; j++) {
        const otherSublineInfo = sublineInfos[j];
        let _crossPoints = intersectCommonShape(sublineInfo.shape, otherSublineInfo.shape);
        if (!_crossPoints) continue;

        _crossPoints = _crossPoints.sort((point1, point2) => {
          return Point2dUtils.distanceTo(point1, rawPoint) - Point2dUtils.distanceTo(point2, rawPoint);
        });
        if (Point2dUtils.distanceTo(_crossPoints[0], rawPoint) > dynamicAbsorbDistance) continue;

        // 底图线参与的交点不享有「交点优先」特权，否则它会压过权重更高的真实墙端点
        const isCrossPoint = !isFloorPlanSubline(sublineInfo) && !isFloorPlanSubline(otherSublineInfo);

        intersected = true;
        this.addAbsorbPoint(_crossPoints[0], sublineInfo, { isCrossPoint });
        this.addAbsorbPoint(_crossPoints[0], otherSublineInfo, { isCrossPoint });
      }

      // 如果没有交点，添加垂足
      if (!intersected && !sublineInfo.extra?.skipFallbackAbsorb) {
        this.addAbsorbPoint(sublineInfo.absorbPoint, sublineInfo);
      }
    }
  }

  private absorbPointMap: Map<number, IAbsorbPointRecord> = new Map();
  private addAbsorbPoint(point: IPoint2d, sublineInfo: ISublineInfo, options?: { isCrossPoint?: boolean }) {
    let pointHash = genPointHash(point, thresholdValueMagnitude);
    const candidatePointType = options?.isCrossPoint ? EAbsorbPointType.Cross : sublineInfo.absorbPointType;

    const nearNode = this._pointRTree.searchNearNode(point);
    if (nearNode) {
      pointHash = nearNode.infos[0].pointHash;
    } else {
      this._pointRTree.addPoint(point, { pointHash });
    }

    const absorbPointInfo = this.absorbPointMap.get(pointHash);
    if (absorbPointInfo) {
      if (this.shouldUseCandidateAbsorbPoint(absorbPointInfo, candidatePointType)) {
        absorbPointInfo.absorbPoint = point;
      }
      absorbPointInfo.absorbPointType = getPreferredDisplayAbsorbPointType(
        absorbPointInfo.absorbPointType,
        candidatePointType
      );
      absorbPointInfo.hasCrossPoint = absorbPointInfo.hasCrossPoint || !!options?.isCrossPoint;
      absorbPointInfo.sublineInfos.add(sublineInfo);
    } else {
      this.absorbPointMap.set(pointHash, {
        absorbPoint: point,
        absorbPointType: candidatePointType,
        sublineInfos: new Set([sublineInfo]),
        hasCrossPoint: !!options?.isCrossPoint
      });
    }
  }

  private shouldUseCandidateAbsorbPoint(absorbPointInfo: IAbsorbPointRecord, candidatePointType: EAbsorbPointType) {
    if (candidatePointType === EAbsorbPointType.Cross) return true;
    if (absorbPointInfo.hasCrossPoint) return false;

    return (
      getDisplayAbsorbPointTypePriority(candidatePointType) >
      getDisplayAbsorbPointTypePriority(absorbPointInfo.absorbPointType)
    );
  }

  private getSortedAbsorbPointList(rawPoint: IPoint2d) {
    // 添加距离参数
    let neiborAbsorbPointList = Array.from(this.absorbPointMap.values());
    const currentAbsorbOrthogonal = this._currentAbsorbOrthogonal;
    const orthogonalLine =
      currentAbsorbOrthogonal === ESublineType.HorizontalLine ? this._horizontalLine : this._verticalLine;
    if (this._forceOrthogonalAbsorb && orthogonalLine && currentAbsorbOrthogonal) {
      // 如果是强制吸附正交线，则只考虑正交线上的吸附点或端点在正交线附近
      neiborAbsorbPointList = neiborAbsorbPointList.filter((item) =>
        Array.from(item.sublineInfos).some(
          ({ type, absorbPoint }) =>
            type === currentAbsorbOrthogonal || orthogonalLine.pointToLineDistance(absorbPoint) < thresholdValue
        )
      );
    }
    const absorbPointList = neiborAbsorbPointList
      .map((item) => {
        const sublineInfos = Array.from(item.sublineInfos);
        const distanceToRawPoint = Point2dUtils.distanceTo(item.absorbPoint, rawPoint);
        return {
          ...item,
          // 全部辅助线都来自底图的候选点排在最后：吸附到它不会与既有墙体形成连接
          isPureFloorPlan: sublineInfos.every(isFloorPlanSubline) ? 1 : 0,
          absorbPriority: item.hasCrossPoint ? 1 : 0,
          weight: this.evaluatePointWeight(sublineInfos),
          distanceToRawPoint
        };
      })
      .sort((a, b) => {
        if (a.isPureFloorPlan !== b.isPureFloorPlan) return a.isPureFloorPlan - b.isPureFloorPlan; // 升序
        if (a.absorbPriority !== b.absorbPriority) return b.absorbPriority - a.absorbPriority;
        if (a.weight !== b.weight) return b.weight - a.weight;
        return a.distanceToRawPoint - b.distanceToRawPoint; // 升序
      });

    return absorbPointList;
  }

  private evaluatePointWeight(relativeSublineInfos: ISublineInfo[]) {
    let weight = 0;
    const step = 100;
    const wallSegStep = 2;
    const normalStep = 1;

    for (const sublineInfo of relativeSublineInfos) {
      if (
        sublineInfo.absorbPointType === EAbsorbPointType.Start ||
        sublineInfo.absorbPointType === EAbsorbPointType.End
      ) {
        weight += step;
      }

      if (sublineInfo.absorbPointType === EAbsorbPointType.Center) {
        weight += step;
      }

      switch (sublineInfo.type) {
        case ESublineType.WallSeg:
          weight += wallSegStep;
          break;
        case ESublineType.WallExtensionLine:
        case ESublineType.StartNormalLine:
        case ESublineType.EndNormalLine:
        case ESublineType.HorizontalLine:
        case ESublineType.VerticalLine:
          weight += normalStep;
          break;
      }
    }

    return weight;
  }

  private _prepareWallSubLineByLine(startPoint: IPoint2d, endPoint: IPoint2d) {
    const currentWallLineSeg = new LineSeg2d(startPoint, endPoint);
    const currentWallLine = new Line2d({ points: { p1: startPoint, p2: endPoint } });
    const wallInfos = this._totalWallInfos;
    const dynamicAbsorbDistance = globalStores.dynamicAbsorbDistance;

    // 弧形墙/弧形隔墙没有可用于平行吸附的中线，其弦只是辅助几何，不能作为吸附参考，
    // 因此弧形墙一律视为不平行，只走后面的端点法线吸附
    const wallParallelCfg = wallInfos.map((wallInfo) => {
      if (wallInfo.center) return false;
      return currentWallLineSeg.isParallel(new LineSeg2d(wallInfo.start, wallInfo.end));
    });
    let sublineInfos: ISublineInfo[] = [];
    const addedLineHash = this._addedLineHash;

    //计算墙线段/墙切线
    for (let i = 0; i < wallInfos.length; i++) {
      const wallInfo = wallInfos[i];
      const wallSeg = wallInfo.seg;
      const isParallel = wallParallelCfg[i];

      // 不平行不吸附
      if (!isParallel) continue;
      const distance = currentWallLine.pointToLineDistance(wallInfo.start);

      if (distance > dynamicAbsorbDistance) continue;
      const startFootRatio = getFootRatio(wallInfo.seg, startPoint);
      const endFootRatio = getFootRatio(wallInfo.seg, endPoint);
      const startFootPoint = getTangentFootPoint(wallSeg, startPoint, startFootRatio);
      const endFootPoint = getTangentFootPoint(wallSeg, endPoint, endFootRatio);

      const lineHash = genShapeHash(wallInfo.shape);
      const isAddedLine = this._addedLineHash.has(lineHash);

      if ((startFootRatio < 0 && endFootRatio < 0) || (startFootRatio > 1 && endFootRatio > 1)) {
        // 两线段不重叠
        let start = startFootRatio < endFootRatio ? endFootPoint : startFootPoint;
        let end = wallInfo.start;
        if (startFootRatio > 1 && endFootRatio > 1) {
          start = startFootRatio > endFootRatio ? endFootPoint : startFootPoint;
          end = wallInfo.end;
        }
        if (wallInfo.id) {
          // 吸附了墙中线及其延长线
          end =
            Point2dUtils.distanceTo(wallInfo.start, start) > Point2dUtils.distanceTo(wallInfo.end, start)
              ? wallInfo.start
              : wallInfo.end;
        }

        const shape = wallInfo.center ? new Line2d({ points: { p1: start, p2: end } }) : wallInfo.shape;
        sublineInfos.push({
          wallId: wallInfo.id,
          type: ESublineType.WallExtensionLine,
          absorbPoint: end,
          origin: start,
          absorbPointType: EAbsorbPointType.Line,
          shape,
          source: wallInfo.source,
          extra: getArcCenterlineExtra(wallInfo.seg)
        });
      } else if (!isAddedLine) {
        // 部分重叠 OR 投影完全被包含 OR 投影完全包含墙体
        const shape = wallInfo.center
          ? new Line2d({ points: { p1: wallInfo.start, p2: wallInfo.end } })
          : wallInfo.shape;
        sublineInfos.push({
          wallId: wallInfo.id,
          type: ESublineType.WallExtensionLine,
          absorbPoint: wallInfo.end,
          origin: wallInfo.start,
          absorbPointType: EAbsorbPointType.Line,
          shape,
          source: wallInfo.source,
          extra: getArcCenterlineExtra(wallInfo.seg)
        });
      }

      addedLineHash.add(lineHash);
    }

    for (let i = 0; i < wallInfos.length; i++) {
      const wallInfo = wallInfos[i];
      const isParallel = wallParallelCfg[i];

      if (isParallel) continue;

      for (const normalLineInfo of this.getWallNormalLineInfos(wallInfo, {
        includeChordEndpointNormalLines: false
      })) {
        const normalLineHash = genShapeHash(normalLineInfo.line);
        if (addedLineHash.has(normalLineHash) || !currentWallLine.isParallelTo(normalLineInfo.line)) continue;

        addedLineHash.add(normalLineHash);
        const { footPoint, nearestPoint } = getProjectClosestEndpoints(
          normalLineInfo.line,
          normalLineInfo.origin,
          startPoint,
          endPoint
        );
        const distance = Point2dUtils.distanceTo(nearestPoint, footPoint);
        if (distance < dynamicAbsorbDistance) {
          sublineInfos.push({
            wallId: normalLineInfo.wallId,
            type: normalLineInfo.type,
            absorbPoint: footPoint,
            origin: normalLineInfo.origin,
            absorbPointType: EAbsorbPointType.Line,
            shape: normalLineInfo.line,
            source: normalLineInfo.source
          });
        }
      }
    }

    // 先获取端点吸附的点，不参与后续相交计算
    sublineInfos = sublineInfos.filter((sublineInfo) => {
      let preserve = true;
      if (sublineInfo.absorbPointType === EAbsorbPointType.Start) {
        this.addAbsorbPoint(sublineInfo.absorbPoint, sublineInfo);
        preserve = false;
      } else if (sublineInfo.absorbPointType === EAbsorbPointType.End) {
        this.addAbsorbPoint(sublineInfo.absorbPoint, sublineInfo);
        preserve = false;
      }
      return preserve;
    });

    this._sublineInfos.push(...sublineInfos);
  }

  private _sublineIntersectWhenCalculateByLineSeg(startPoint: IPoint2d, endPoint: IPoint2d) {
    // 开始计算交点
    const dynamicAbsorbDistance = globalStores.dynamicAbsorbDistance;
    const lineSeg = new LineSeg2d(startPoint, endPoint);
    const sublineInfos = this._sublineInfos;
    for (let i = 0; i < sublineInfos.length; i++) {
      const sublineInfo = sublineInfos[i];
      let intersected = false;

      for (let j = i + 1; j < sublineInfos.length; j++) {
        const otherSublineInfo = sublineInfos[j];
        let _crossPoints = intersectCommonShape(sublineInfo.shape, otherSublineInfo.shape);
        if (!_crossPoints) continue;

        _crossPoints = _crossPoints.sort((point1, point2) => {
          return lineSeg.pointToLineDistance(point1) - lineSeg.pointToLineDistance(point2);
        });
        if (lineSeg.pointToLineDistance(_crossPoints[0]) > dynamicAbsorbDistance) continue;

        intersected = true;
        this.addAbsorbPoint(_crossPoints[0], sublineInfo);
        this.addAbsorbPoint(_crossPoints[0], otherSublineInfo);
      }

      // 如果没有交点，添加垂足
      if (!intersected) {
        this.addAbsorbPoint(sublineInfo.absorbPoint, sublineInfo);
      }
    }
  }

  private clear() {
    this._pointRTree.clear();
    this.absorbPointMap.clear();
    this._sublineInfos = [];
    this._addedLineHash.clear();
    this._extraWallInfos = [];
    // 归零而不是留着上一帧的矩形：新增 calculate 入口时漏调 _updateFilterBounds 只会退化成不过滤
    this._filterBounds = null;
  }
}

function getArcCenterlineExtra(wallSeg: IShapeSeg2d): ISublineInfo['extra'] | undefined {
  if (!(wallSeg instanceof ArcSeg2d)) return undefined;

  return {
    arcCenterline: wallSeg
  };
}

/**
 * 获取线段在直线的投影点中距离直线指定点距离最小的点
 */
function getProjectClosestEndpoints(line: Line2d, target: IPoint2d, otherLineStart: IPoint2d, otherLineEnd: IPoint2d) {
  const shadowOtherStart = line.pointProjectOnLine(otherLineStart);
  const shadowOtherEnd = line.pointProjectOnLine(otherLineEnd);
  const shadowTarget = line.pointOnLine(target) ? target : line.pointProjectOnLine(target);

  const pairs = [
    [shadowOtherStart, otherLineStart],
    [shadowOtherEnd, otherLineEnd],
    [shadowOtherStart, otherLineStart],
    [shadowOtherEnd, otherLineEnd]
  ];

  let footPoint = shadowOtherStart;
  let nearestPoint = otherLineStart;
  let minDistSq = Infinity;

  for (const [shadowPoint, rawPoint] of pairs) {
    const distSq = (shadowPoint.x - shadowTarget.x) ** 2 + (shadowPoint.y - shadowTarget.y) ** 2;
    if (distSq < minDistSq) {
      minDistSq = distSq;
      footPoint = shadowPoint;
      nearestPoint = rawPoint;
    }
  }

  return {
    footPoint,
    nearestPoint
  };
}
